import { toSignal } from '@angular/core/rxjs-interop';
import { ContactsTenantScopeService } from '@modules/contacts/services/contacts-tenant-scope.service';
import { contactsOwnerContext } from '@modules/contacts/services/contacts-owner-context';
import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { BaseStitchListComponent, StitchListItem } from '@modules/common/components/list/base-stitch-list.component';
import { ScrollToPosition } from '@modules/common/services/scroll-to-index.injection-token';
import { Like } from '@modules/common/types/like';
import { type StitchCardSelection, type StitchCardVariant } from '@modules/common/utils/stitch-card-variant';
import { GroupsService } from '@modules/contacts/services/groups.service';
import { ContactsAppStateService } from '@modules/contacts/services/state.service';
import { Contact } from '@modules/contacts/types/contact';
import { Group } from '@modules/contacts/types/group';
import { GroupsFilters } from '@modules/contacts/types/groups-filters';
import { type SelectableOpenEvent } from '@modules/drag-n-drop/types/selectable-open-event';
import { GroupComponent } from '@modules/elements/components/group/group.component';
import { LinkedInfoService } from '@modules/linked-info/services/linked-info.service';
import type { KnotSource } from '@sharedlib/common/types/knowledge/knot-source';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { BehaviorSubject, merge, Observable, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-groups-list',
  templateUrl: './groups-list.component.html',
  styleUrls: ['./groups-list.component.less'],
  providers: [{ provide: ScrollToPosition, useFactory: () => new BehaviorSubject<number>(null) }],
  standalone: false,
})
export class GroupsListComponent extends BaseStitchListComponent<Group> implements OnInit, OnDestroy, OnChanges {
  public readonly tenantItems = input<ReadonlyArray<{ readonly accountId?: string }>>();

  /** Show each row's hover quick actions; off for lists that only preview their items. */
  public readonly actionsButtonEnabled = input<boolean>(true);

  /** Container treatment forwarded to every card; the knowledge panel asks for `'card'`. */
  public readonly variant = input<StitchCardVariant>('row');

  /** Id of the item open in the dynamic panel, forwarded so that card draws itself as selected. */
  public readonly previewedItemId = input<string | null>(null);

  /** What each card draws as selected, forwarded; the knowledge panel asks for the list's own selection. */
  public readonly cardSelection = input<StitchCardSelection>('preview');

  /** The knowledge panel draws its results as cards; every other caller keeps the app's flush row. */
  protected readonly isCard = computed(() => this.variant() === 'card');

  @Input()
  public override filters: Like<GroupsFilters> = {};
  @Input()
  public withTags = false;
  @Input()
  public withKnots = false;
  @Input()
  public knotsSource: KnotSource;
  @Input()
  public debug: 'score';

  @Output()
  public readonly clickGroup = new EventEmitter<Group>();
  @Output()
  public readonly openGroup = new EventEmitter<Group>();
  @Output()
  public readonly openContact = new EventEmitter<Contact>();
  @Output()
  public readonly doubleClickGroup = new EventEmitter<Group>();

  @ViewChildren(GroupComponent, { read: ElementRef<HTMLElement> })
  public override itemElements: QueryList<ElementRef<HTMLElement>>;

  public override items: StitchListItem<Group>[] = [];

  private readonly groupsService = inject(GroupsService);
  private readonly contactsTenantScope = inject(ContactsTenantScopeService, { optional: true });
  private readonly settledTenant = toSignal(
    this.contactsTenantScope?.selectedTeamId?.() ?? of(this.contactsTenantScope?.tenantId?.()),
    { initialValue: undefined },
  );
  private readonly contactsStateService = inject(ContactsAppStateService);
  private readonly linkedInfoService = inject(LinkedInfoService);

  protected override getStitchType(): StitchType {
    return StitchType.GROUP;
  }

  public override ngOnInit() {
    merge(
      // Global refresh button
      this.contactsStateService.getRefreshAll(),
      // Subscribe for linked info updates
      this.linkedInfoService.getRefresh(),
      this.groupsService.getRefresh(),
    )
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.loadPage(this.page);
      });

    super.ngOnInit();
  }

  public ngOnChanges(changes: SimpleChanges) {
    const ownerChanged =
      changes.tenantItems &&
      JSON.stringify(changes.tenantItems.previousValue?.map(({ accountId }) => accountId)) !==
        JSON.stringify(changes.tenantItems.currentValue?.map(({ accountId }) => accountId));
    if ('filters' in changes || ownerChanged) {
      this.clearItems();
      this.resetItems();
    }
  }

  protected override getItems(offset: number, limit: number): Observable<{ items: Group[]; count: number }> {
    const requestContext = contactsOwnerContext(this.tenantItems(), this.settledTenant());
    if ((this.tenantItems() !== undefined || this.contactsTenantScope?.tenantId) && !requestContext) {
      return of({ items: [], count: 0 });
    }
    return this.groupsService.search(
      { ...this.filters, limit, offset },
      {
        withTags: this.withTags,
        withKnots: this.withKnots,
        withConnections: false,
        knotsSource: this.knotsSource,
        requestContext,
      },
    );
  }

  protected override stitchSignalSpaceIds(): ReadonlyArray<string> {
    const owners = this.tenantItems()
      ?.map(({ accountId }) => accountId)
      .filter(Boolean);
    if (owners) return owners;
    const tenant = this.contactsTenantScope?.effectiveTenantId?.();
    return tenant ? [tenant] : super.stitchSignalSpaceIds();
  }

  public handleDoubleClickItem(group: Group) {
    this.doubleClickGroup.emit(group);
  }

  public handleOpenSelected(event: SelectableOpenEvent<Group>): void {
    this.clickGroup.emit(event.item.data);
  }
}
